import type { FollowUpCategoryTypes } from '@/shared/api/graphql/account';
import type { RpmEnrolledAccounts } from '@/shared/api/graphql/remotePatientMonitoring';
import type { VisitMode } from '@/shared/lib/types/followup';
import type { JSONData } from '@/shared/ui/JsonKit';
import type { ReactNode } from 'react';
import type { ClinicalCareEncounterType } from './clinicalCare';
import type { AccountType, RelyingParty, RelyingPartyAdminType } from './graphql';
import type { PatientReviewNoteType, RPMAccountAlert, RpmFollowupConsultType } from './remotePatientMonitoring';

export type FormValues = {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  address?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressState?: string;
  addressCity?: string;
  email?: string;
  zipcode?: string;
  ehrId?: string;
  mtmCaseNumber?: string;
  healthPlan?: string;
  phone?: string;
  birthDate?: Date | null;
  gender?: string;
  consent?: string;
  emergencyContactName?: string;
  emergencyContactEmail?: string;
  emergencyContactPhone?: string;
  doNotCall?: boolean;
  primaryLanguage?: string;
  doNotCallLastSpecifiedAt?: Date | null;
  alternateRpmProviderId?: string;
  usualProviderId?: string;
  homePhone?: string;
  consentToCall?: boolean;
  consentToText?: boolean;
  contactPreference?: string;
  race?: string;
  ethnicityCode?: string;
  maritalStatus?: string;
  guarantorRelationship?: string;
  guarantorFirstName?: string;
  guarantorLastName?: string;
  guarantorMiddleName?: string;
  guarantorBirthDate?: Date | null;
  guarantorPhone?: string;
  guarantorEmail?: string;
  guarantorAddressLine1?: string;
  guarantorAddressLine2?: string;
  guarantorAddressCity?: string;
  guarantorAddressState?: string;
  guarantorZipcode?: string;
  guarantorAddressSource?: GuarantorAddressSource;
  guarantorIsEmergencyContact?: boolean;
  guarantorCanAccessMedicalRecords?: boolean;
};

export type User = {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  verifiedEmail?: string | null;
};

export type AccountContactAddress = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
};

export type AccountContact = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  middleName?: string | null;
  phone?: string | null;
  email?: string | null;
  dob?: string | null;
  relationshipType?: string | null;
  isGuarantor: boolean;
  isEmergencyContact: boolean;
  canAccessMedicalRecords: boolean;
  addresses?: AccountContactAddress[] | null;
};

export type AccountComment = {
  id: string;
  body: string;
  createdAt: string;
  sender: {
    name: string;
    email: string;
  };
};

export interface GetAccountCommentsResponse {
  commentsByAccount: {
    comments: AccountComment[];
    meta: {
      totalPages: number;
      totalCount: number;
    };
  };
}

export type Profile = {
  addressLine1?: string;
  addressLine2?: string;
  addressState?: string;
  addressCity?: string;
  zipcode?: string;
  healthPlan?: string;
  phone?: string;
  consent?: string;
  birthDate?: string;
  gender?: string;
  emergencyContactName?: string;
  emergencyContactEmail?: string;
  emergencyContactPhone?: string;
  userProfile?: User;
};

export type HealthPlan = {
  id: string;
  orgName: string;
};

export type FollowUpRemindersTypes = {
  id: string;
  followUpOn: string;
  type: string;
  subType: string;
  notes: string;
  status: string;
  mode?: VisitMode | null;
  /** The visit's appointment reminder setting. */
  appointmentRemindersEnabled?: boolean;
  relyingPartyAdmin: RelyingPartyAdminType;
  relyingParty: RelyingParty;
  createdAt: string;
  account: AccountType;
  category: FollowUpCategoryTypes;
  rpmAlert: RPMAccountAlert | null;
  createdByRelyingPartyAdmin: RelyingPartyAdminType | null;
  immediateActions: JSONData;
  rpmAccountReviewNote: PatientReviewNoteType;
  rpmFollowupConsult: Pick<RpmFollowupConsultType, 'id' | 'consultAt' | 'status' | 'signOffAt' | 'isTelehealthVisitLaunched'> | null;
  clinicalCareEncounter: Pick<
    ClinicalCareEncounterType,
    'id' | 'startedAt' | 'encounterMode' | 'status' | 'signOffAt' | 'isTelehealthVisitLaunched'
  > | null;
};

type FollowupFormHiddenFields = {
  type: boolean;
  subType: boolean;
  status: boolean;
};

type FollowupFormCustomLabels = {
  performedDate: string;
  performedTimeHour: string;
  performedTimeMinute: string;
  type: string;
  assignee: string;
  subType: string;
  status: string;
  note: string;
};

export type InitialVisitFollowupCustomTypes = {
  hiddenFields: Partial<FollowupFormHiddenFields>;
  customLabels: Partial<FollowupFormCustomLabels>;
};

export const GENDER_OPTIONS_ARRAY = ['Male', 'Female'];

export const GENDER_OPTIONS = [
  { label: 'Male', value: 'Male' },
  { label: 'Female', value: 'Female' }
];

// Single source of truth for the guarantor statement address
export type GuarantorAddressSource = 'patient' | 'company' | 'custom';

export const RELATIONSHIP_TO_GUARANTOR_OPTIONS = [
  { label: 'Self', value: 'Self' },
  { label: 'Spouse', value: 'Spouse' },
  { label: 'Child', value: 'Child' },
  { label: 'Other', value: 'Other' }
];

export const CONSENT_OPTIONS = [
  { label: 'Patient has not consented', value: 'NotProvided' },
  { label: 'Patient has provided consent', value: 'PatientAnyProvider' },
  { label: "Patient's guardian has provided consent", value: 'GuardianAnyProvider' }
];

export enum AccountNameOrder {
  FIRST_LAST = 'first-last',
  LAST_FIRST = 'last-first'
}

export type PatientDetailsSegmentConfig = {
  key: string;
  label: string;
  value: (patientInfo: RpmEnrolledAccounts) => string | number | null | undefined | ReactNode;
  color?: (patientInfo: RpmEnrolledAccounts) => string | undefined;
  hideIfEmpty?: boolean;
  tooltip?: (patientInfo: RpmEnrolledAccounts) => string | undefined;
  onClick?: (patientInfo: RpmEnrolledAccounts) => void;
};

export enum AccountMedicalServiceType {
  BLOOD_PRESSURE = 'Blood Pressure',
  BLOOD_GLUCOSE = 'Blood Glucose'
}
